import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import toast from "react-hot-toast";
import {
  login as reduxLogin,
  getMyPermissions,
} from "../redux/actions/authActions";
import { useAuth } from "../context/AuthContext";
import LogoImg from "../images/Ayushman_Bharat_logo.png";
import LoginBg from "../images/login.png";
import {
  FiUser,
  FiLock,
  FiEye,
  FiEyeOff,
  FiLogIn,
  FiShield,
  FiCheckCircle,
  FiMail,
} from "react-icons/fi";

const Login = () => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, login } = useAuth();

  useEffect(() => {
    if (isAuthenticated) {
      const from = location.state?.from?.pathname || "/dashboard";
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, navigate, location]);

  useEffect(() => {
    if (isAuthenticated) {
      window.history.pushState(null, "", window.location.href);
    }
  }, [isAuthenticated]);

  const handleLoginSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;
    setLoading(true);

    try {
      const response = await dispatch(reduxLogin(username, password));

      if (response?.meta?.status) {
        const {
          token,
          role,
          id,
          name,
          phone,
          municipality,
          district,
          role_id,
        } = response.data;

        // Store user data
        localStorage.setItem("userId", id);
        localStorage.setItem("name", name);
        localStorage.setItem("phone", phone);
        localStorage.setItem("municipality", municipality || "");
        localStorage.setItem("district", district || "");
        localStorage.setItem("role_id", role_id || "");
        localStorage.setItem("role", role);

        if (rememberMe) {
          localStorage.setItem("rememberMe", "true");
        }

        // Update auth context
        login(token, role);

        // Fetch permissions
        await dispatch(getMyPermissions());

        toast.success("Login successful! Welcome back.");
        navigate("/dashboard", { replace: true });
      } else {
        toast.error(response?.meta?.message || "Invalid credentials.");
      }
    } catch (err) {
      toast.error(err.message || "Login error.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen w-full flex items-center justify-center p-4 relative bg-cover bg-center bg-no-repeat"
      style={{ backgroundImage: `url(${LoginBg})` }}
    >
      <div className="relative z-10 w-full max-w-md">
        <div className="bg-white/90 backdrop-blur-md rounded-2xl p-6 md:p-8 border border-white/30 shadow-xl">
          <div className="text-center mb-6">
            <div className="flex justify-center mb-3">
              <div className="relative">
                <img
                  className="w-20 h-20 object-contain"
                  src={LogoImg}
                  alt="NSAP Logo"
                />
                <div className="absolute -inset-2 bg-orange-400/20 rounded-full blur-xl"></div>
              </div>
            </div>
            <h1 className="text-2xl font-bold text-gray-800">NSAP Survey</h1>
            <p className="text-sm text-gray-600">Sign in to your account</p>
          </div>

          <form
            className="space-y-4"
            onSubmit={handleLoginSubmit}
            autoComplete="off"
          >
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">
                Username
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <FiMail className="h-4 w-4 text-orange-500" />
                </div>
                <input
                  className="w-full pl-9 pr-3 py-2 bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all duration-300 text-gray-800 placeholder-gray-400 text-sm"
                  placeholder="Enter your username"
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  autoComplete="off"
                  required
                  disabled={loading}
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">
                Password
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <FiLock className="h-4 w-4 text-orange-500" />
                </div>
                <input
                  className="w-full pl-9 pr-9 py-2 bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all duration-300 text-gray-800 placeholder-gray-400 text-sm"
                  placeholder="Enter your password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="off"
                  required
                  disabled={loading}
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 transition-colors"
                  onClick={() => setShowPassword(!showPassword)}
                  disabled={loading}
                >
                  {showPassword ? (
                    <FiEyeOff className="h-4 w-4" />
                  ) : (
                    <FiEye className="h-4 w-4" />
                  )}
                </button>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <label className="flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  className="h-3.5 w-3.5 text-orange-600 focus:ring-orange-500 border-gray-300 rounded transition-colors"
                  disabled={loading}
                />
                <span className="ml-1.5 text-xs font-medium text-gray-600">
                  Remember me
                </span>
              </label>
            </div>

            <button
              type="submit"
              disabled={loading || !username || !password}
              className="w-full bg-gradient-to-r from-orange-600 to-orange-500 hover:from-orange-700 hover:to-orange-600 text-white font-semibold py-2.5 px-4 rounded-lg transition-all duration-300 shadow-md shadow-orange-600/30 hover:shadow-lg hover:shadow-orange-600/40 transform hover:-translate-y-0.5 disabled:opacity-50 disabled:transform-none disabled:hover:shadow-md disabled:cursor-not-allowed text-sm"
            >
              <div className="flex items-center justify-center space-x-2">
                {loading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    <span>Signing in...</span>
                  </>
                ) : (
                  <>
                    <FiLogIn className="w-4 h-4" />
                    <span>Sign In</span>
                  </>
                )}
              </div>
            </button>
          </form>

          <div className="mt-4 flex items-center justify-center space-x-2 text-xs">
            <FiShield className="w-3.5 h-3.5 text-gray-500" />
            <span className="text-gray-500 font-medium">
              Secure • Encrypted
            </span>
            <FiCheckCircle className="w-3.5 h-3.5 text-green-500" />
          </div>

          <div className="mt-3 pt-3 border-t border-gray-200">
            <p className="text-center text-[10px] font-medium text-gray-400">
              © {new Date().getFullYear()}{" "}
              <a
                href="https://www.ebluesys.com"
                target="_blank"
                rel="noopener noreferrer"
                className="font-semibold text-orange-500 hover:text-orange-600 transition-colors"
              >
                Ebluesys
              </a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
